import * as React from "react";
import { Divider, TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import LoadingIndicator from "../../LoadingIndicator";
import { useHistory } from "react-router-dom";
import SearchPills from "../../../containers/SearchPills";
import { fileNameFormat } from "../../../common/services/Utilities";
import { summaryTableHeader } from "./SummaryTableUtilities";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import ShowHideButton from "../../ShowHideButton";

const useStyles = makeStyles(theme => ({
  totalsFooter: {
    "& td": {
      background: theme.palette.primary.main,
    },
  },
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  footerText: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 16,
    whiteSpace: "nowrap",
  },
  loadingSpinner: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },
  loadingText: {
    fontSize: 20,
    width: "100%",
    textAlign: "center",
  },
  tableRow: {
    "cursor": "pointer",
    "&:hover td, &:focus-visible td": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

interface Props {
  appState: Query;
}

export default function SummaryTablesByCharter({ appState }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const tableDownloadData: KeyValue[] = [];
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        "Summary_Tables_By_Charter_Class_formatted",
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
    },
  ];

  const [loading, setLoading] = React.useState(false);
  const [bankTypeData, setBankTypeData] = React.useState<KeyValue[]>([{}]);
  const [charterData, setCharterData] = React.useState<KeyValue>({
    commercial: [],
    savings: [],
    reserveMember: [],
    reserveNonMemeber: [],
    fdicSupervised: [],
    federalReserveSupervised: [],
    otsSupervisedSavings: [],
  });
  const [totals, setTotals] = React.useState<KeyValue>({
    institutions: 0,
    offices: 0,
    deposits: 0,
  });
  const [previousYear, setPreviousYear] = React.useState(
    appState.filters.lastYear,
  );
  const [isOTSSupervisedSavings, setIsOTSSupervisedSavings] = React.useState(
    false,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);

  const summaryTablesByCharterMetadata: KeyValue[] = [
    {
      title: "Charter Class",
      apiField: "INSURED",
      styles: { textAlign: "left", width: 200, minWidth: 200 },
    },
    {
      title: "Institutions",
      apiField: "sum_BKMO",
      styles: { textAlign: "center", width: 200, minWidth: 200 },
    },
    {
      title: "Offices",
      apiField: "count",
      styles: { textAlign: "center", width: 200, minWidth: 200 },
    },
    {
      title: "Total Deposits (Thousands USD)",
      apiField: "sum_DEPSUMBR",
      styles: { textAlign: "right", width: 200, minWidth: 200 },
    },
  ];

  const summaryTablesByCharterHeader: TableHeader[][] = [
    summaryTablesByCharterMetadata.map(element => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  React.useEffect(() => {
    if (
      (appState.filters.lastYear !== previousYear || initialLoad) &&
      appState.ui.reportType
    ) {
      setLoading(true);
      initialLoad && setInitialLoad(false);
      setPreviousYear(appState.filters.lastYear);
      let isOTSSupervisedSavings = false;
      if (
        Number(appState.filters.lastYear) >= 1994 &&
        Number(appState.filters.lastYear) <= 2011
      ) {
        isOTSSupervisedSavings = true;
        setIsOTSSupervisedSavings(true);
      }
      let isMounted = true;
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            }&sort_by=INSURED&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:CB&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:CB AND BKCLASS:SM&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:CB AND BKCLASS:NM&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND REGAGNT:FDIC AND CHARTER:STATE&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND REGAGNT:FED&sort_by=CHARTER&sort_order=asc`,
          ),
          isOTSSupervisedSavings &&
            fetchData(
              `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
                appState.filters.lastYear
              } AND INSURED:SA AND REGAGNT:OTS AND CHARTER:STATE&sort_by=CHARTER&sort_order=asc`,
            ),
        ])
          .then(response => {
            if (isMounted) {
              let sumInstitutions = 0;
              let sumOffices = 0;
              let sumDeposits = 0;
              const bankTypeData = response[0].data;

              for (const data of bankTypeData) {
                sumInstitutions = sumInstitutions + data.sum_BKMO;
                sumOffices = sumOffices + data.count;
                sumDeposits = sumDeposits + data.sum_DEPSUMBR;
              }

              const totals = {
                institutions: sumInstitutions,
                offices: sumOffices,
                deposits: sumDeposits,
              };

              setTotals(totals);
              setBankTypeData(bankTypeData);
              setCharterData({
                commercial: response[1].data,
                savings: response[2].data,
                reserveMember: response[3].data,
                reserveNonMemeber: response[4].data,
                fdicSupervised: response[5].data,
                federalReserveSupervised: response[6].data,
                otsSupervisedSavings: isOTSSupervisedSavings
                  ? response[7].data
                  : [],
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [appState.filters, bankTypeData, initialLoad, previousYear, appState.ui]);

  const federalReserveSupervisedRow = charterData.federalReserveSupervised.map(
    (data: KeyValue, index: number) => {
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: "Federal Reserve-Supervised",
      });
      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=Federal Reserve-Supervised&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`FED_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell
            style={{
              fontStyle: "italic",
              textAlign: "left",
              textIndent: "20px",
            }}
          >
            Federal Reserve-Supervised
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const otsSupervisedSavingsRow = charterData.otsSupervisedSavings.map(
    (data: KeyValue, index: number) => {
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: "OTS-Supervised Savings Associations",
      });
      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=OTS-Supervised Savings Associations&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`OTS_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell
            style={{
              fontStyle: "italic",
              textAlign: "left",
              textIndent: "20px",
            }}
          >
            OTS-Supervised Savings Associations
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const downloadOrder = [
    "Commercial Banks",
    "National Charter",
    "State Charter",
    "Federal Reserve Member",
    "Federal Reserve Nonmember",
    "Savings Institutions",
    "Savings Institutions*",
    "Federal Charter Savings Associations",
    "State Charter Savings Institutions",
    "FDIC-Supervised Savings Banks",
    "OTS-Supervised Savings Associations",
    "Federal Reserve-Supervised",
    "U.S. Branches of Foreign Banks",
    "National Totals",
  ];

  const fdicSupervisedBankRow = charterData.fdicSupervised.map(
    (data: KeyValue, index: number) => {
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: "FDIC-Supervised Savings Banks",
      });
      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=FDIC-Supervised Savings Banks&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`FDIC_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell
            style={{
              fontStyle: "italic",
              textAlign: "left",
              textIndent: "20px",
            }}
          >
            FDIC-Supervised Savings Banks
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const federalReserveMemberRow = charterData.reserveMember.map(
    (data: KeyValue, index: number) => {
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: "Federal Reserve Member",
      });
      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=Federal Reserve Member&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`FRSM_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell
            style={{
              fontStyle: "italic",
              textAlign: "left",
              textIndent: "20px",
            }}
          >
            Federal Reserve Member
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const federalReserveNonmemberRow = charterData.reserveNonMemeber.map(
    (data: KeyValue, index: number) => {
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: "Federal Reserve Nonmember",
      });

      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=Federal Reserve Nonmember&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`FRNM_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell
            style={{
              fontStyle: "italic",
              textAlign: "left",
              textIndent: "20px",
            }}
          >
            Federal Reserve Nonmember
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const bankTypeRows = bankTypeData.map((data: KeyValue, index: number) => {
    const classLookup: KeyValue = {
      CB: "Commercial Banks",
      SA: `Savings Institutions${
        appState.filters.lastYear === "1994" ? "*" : ""
      }`,
      IB: "U.S. Branches of Foreign Banks",
    };
    tableDownloadData.push({
      ...data,
      YEAR: appState.filters.lastYear,
      CLASS: classLookup[data.INSURED],
    });

    const tableRowClick = () => {
      history.push({
        search: `?summaryTableLevelTwo=true&summaryTableType=${
          classLookup[data.INSURED]
        }&reportType=charterClass`,
      });
    };
    return (
      <TableRow
        className={classes.tableRow}
        tabIndex={0}
        key={`BT_${data.INSURED}_${index}`}
        onClick={tableRowClick}
        onKeyPress={(e: any) => {
          if (e.key === "Enter") {
            tableRowClick();
          }
        }}
      >
        <TableCell style={{ fontWeight: "bold", textAlign: "left" }}>
          {classLookup[data.INSURED]}
        </TableCell>
        <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
          {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
        </TableCell>
        <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
          {data.count ? data.count.toLocaleString() : data.count}
        </TableCell>
        <TableCell style={{ fontWeight: "bold", textAlign: "right" }}>
          {data.sum_DEPSUMBR
            ? data.sum_DEPSUMBR.toLocaleString()
            : data.sum_DEPSUMBR}
        </TableCell>
      </TableRow>
    );
  });

  const commercialBanksCharterRows = charterData.commercial.map(
    (data: KeyValue, index: number) => {
      const charterCommercialLookup: KeyValue = {
        FED: "National Charter",
        STATE: "State Charter",
      };
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: charterCommercialLookup[data.CHARTER],
      });

      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=${
            charterCommercialLookup[data.CHARTER]
          }&reportType=charterClass`,
        });
      };

      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          id={`charterSelectionRow-CB-${index}`}
          key={`CB_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell style={{ textAlign: "left", textIndent: "10px" }}>
            {charterCommercialLookup[data.CHARTER]}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const savingsBanksCharterRows = charterData.savings.map(
    (data: KeyValue, index: number) => {
      const charterSavingsLookup: KeyValue = {
        FED: "Federal Charter Savings Associations",
        STATE: "State Charter Savings Institutions",
      };
      tableDownloadData.push({
        ...data,
        YEAR: appState.filters.lastYear,
        CLASS: charterSavingsLookup[data.CHARTER],
      });

      const tableRowClick = () => {
        history.push({
          search: `?summaryTableLevelTwo=true&summaryTableType=${
            charterSavingsLookup[data.CHARTER]
          }&reportType=charterClass`,
        });
      };
      return (
        <TableRow
          className={classes.tableRow}
          tabIndex={0}
          key={`SB_${data.CHARTER}_${index}`}
          onClick={tableRowClick}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              tableRowClick();
            }
          }}
        >
          <TableCell style={{ textAlign: "left", textIndent: "10px" }}>
            {charterSavingsLookup[data.CHARTER]}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.sum_BKMO ? data.sum_BKMO.toLocaleString() : data.sum_BKMO}
          </TableCell>
          <TableCell style={{ textAlign: "center" }}>
            {data.count ? data.count.toLocaleString() : data.count}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : data.sum_DEPSUMBR}
          </TableCell>
        </TableRow>
      );
    },
  );

  const totalsRow = (
    <TableRow className={classes.totalsFooter}>
      <TableCell
        style={{ color: "white", textAlign: "left", fontWeight: "bold" }}
      >
        National Totals
      </TableCell>
      <TableCell
        style={{ color: "white", textAlign: "center", fontWeight: "bold" }}
      >
        {totals.institutions.toLocaleString()}
      </TableCell>
      <TableCell
        style={{ color: "white", textAlign: "center", fontWeight: "bold" }}
      >
        {totals.offices.toLocaleString()}
      </TableCell>
      <TableCell
        style={{ color: "white", textAlign: "right", fontWeight: "bold" }}
      >
        {totals.deposits.toLocaleString()}
      </TableCell>
    </TableRow>
  );
  tableDownloadData.push({
    YEAR: appState.filters.lastYear,
    CLASS: "National Totals",
    count: totals.offices.toLocaleString(),
    sum_BKMO: totals.institutions.toLocaleString(),
    sum_DEPSUMBR: totals.deposits.toLocaleString(),
  });
  const tableBody = loading ? (
    <LoadingIndicator
      text="Loading"
      spinnerSize={32}
      textClass={classes.loadingText}
      spinnerClass={classes.loadingSpinner}
      table
    />
  ) : (
    <>
      {bankTypeData[0]?.INSURED === "CB" && bankTypeRows[0]}
      {commercialBanksCharterRows[0]}
      {commercialBanksCharterRows[1]}
      {federalReserveMemberRow[0]}
      {federalReserveNonmemberRow[0]}
      {Object.keys(bankTypeData).length > 1 && bankTypeData[1]?.INSURED === "SA"
        ? bankTypeRows[1]
        : bankTypeRows[2]}
      {savingsBanksCharterRows[0]}
      {savingsBanksCharterRows[1]}
      {fdicSupervisedBankRow[0]}
      {federalReserveSupervisedRow[0]}
      {isOTSSupervisedSavings && otsSupervisedSavingsRow[0]}
      {Object.keys(bankTypeData).length > 1 &&
        bankTypeData[1]?.INSURED === "IB" &&
        bankTypeRows[1]}
      {totalsRow}
    </>
  );
  const sortedDownload = tableDownloadData.sort(
    (a, b) => downloadOrder.indexOf(a.CLASS) - downloadOrder.indexOf(b.CLASS),
  );
  const downloadHeaderFormat = sortedDownload.map((data: KeyValue) => {
    const returnObj = {
      "Report Period": `6/30/${data.YEAR}`,
      "Charter Class": data.CLASS,
      "Institutions": data.sum_BKMO?.toLocaleString(),
      "Offices": data.count?.toLocaleString(),
      "Total Deposits (Thousands USD)": data.sum_DEPSUMBR?.toLocaleString(),
    };
    return returnObj;
  });

  const headingTitle = (
    <>
      <Typography className={classes.heading}>
        {summaryTableHeader(appState.ui.reportType)}
      </Typography>
      <div style={{ position: "relative", right: 0, top: 5 }}>
        <ApiDataDownload
          app="SOD"
          apiData={apiData}
          downloadData={downloadHeaderFormat}
        />
      </div>
    </>
  );

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <MultiHeaderTable
        tableBody={tableBody}
        tableHeaders={summaryTablesByCharterHeader}
      />
      <Typography className={classes.footerText}>
        Includes deposits of all FDIC-Insured Institutions in domestic offices
        (50 states and DC), Puerto Rico, and U.S. Territories
      </Typography>
      {appState.filters.lastYear === "1994" && (
        <Typography className={classes.footerText}>
          * Includes 18 conservatorships
        </Typography>
      )}
    </>
  );
}
